import client from './base'

export type InvoiceStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'cancelled'
export type PaymentMethod = 'cash' | 'check' | 'wire' | 'mobile' | 'other'
export type InconsistencyAction = 'create_first' | 'update_draft' | 'complement'

export interface CabinetInvoicingSettings {
  id: number

  // Cabinet identity shown on the PDF header
  address: string
  phone: string
  email: string

  // Moroccan legal identifiers (DGI-required)
  ice: string
  if_number: string
  rc: string
  patente: string
  cnss: string

  // TVA configuration
  tva_enabled: boolean
  tva_number: string
  default_tva_rate: string

  // Invoice numbering — auto-increments on every invoice creation
  invoice_prefix: string
  next_invoice_number: number // read-only

  // Bank details printed in PDF payment section
  bank_name: string
  bank_rib: string
  bank_iban: string

  default_payment_terms_days: number
  invoice_footer: string

  created_at: number
  updated_at: number
}

export interface ClientInvoiceListItem {
  id: string
  number: string
  issue_date: string
  due_date: string | null
  status: InvoiceStatus

  client: string
  client_name: string
  case: string | null

  total_ht: string
  total_ttc: string
  net_a_payer: string
  total_paid: string
  reste_a_payer: string

  added_by: { id: string; name: string; is_deleted: boolean }
  created_at: number
  updated_at: number
}

export interface ClientInvoiceLine {
  id: string
  description: string
  quantity: string
  unit_price: string
  tva_active: boolean
  tva_rate: string
  ordering: number

  line_total_ht: string
  line_total_tva: string
  line_total_ttc: string
}

// Unified payment record — replaces the old CaseDeposit and ClientPayment models
export interface CasePayment {
  id: string
  case: number | null      // Case.id (integer) — null for caseless invoices
  invoice: string | null   // ClientInvoice UUID — null when is_provision=true
  amount: string
  payment_date: string     // "YYYY-MM-DD"
  payment_method: PaymentMethod
  reference: string | null
  notes: string | null
  is_provision: boolean    // true when invoice is null (pre-invoicing deposit)
  added_by: { id: string; name: string; is_deleted: boolean }
  created_at: number
  updated_at: number
}

export interface ClientInvoiceDetail extends ClientInvoiceListItem {
  cabinet: string
  notes: string | null

  ras_tva_active: boolean
  ras_tva_rate: string
  ras_ir_active: boolean
  ras_ir_rate: string

  total_tva: string
  montant_ras_tva: string
  montant_ras_ir: string

  pdf_path: string | null

  lines: ClientInvoiceLine[]
  payments: CasePayment[]
}

export interface InvoiceSummary {
  id: string
  number: string
  status: InvoiceStatus
  issue_date: string
  due_date: string | null
  total_ht: string
  total_ttc: string
  net_a_payer: string
  total_paid: string
  reste_a_payer: string
}

export interface ProvisionSummary {
  id: string
  amount: string
  reference: string | null
  payment_date: string
  payment_method: PaymentMethod
  invoice: string | null
}

export interface CaseBillingSummary {
  honoraire_ht: string
  fee_type: 'fixed' | 'percentage'

  total_invoiced_ht: string
  invoicing_gap_ht: string         // honoraire_ht − total_invoiced_ht (negative = over-invoiced)
  is_invoicing_consistent: boolean

  inconsistency_action: InconsistencyAction | null
  editable_invoice_id: string | null  // set only when action = "update_draft"

  payment_target: 'invoice' | 'deposit'
  payment_invoice_id: string | null

  total_deposits_free: string
  total_formally_paid: string
  total_received: string
  remaining_to_receive: string

  open_invoice: InvoiceSummary | null
  invoices: InvoiceSummary[]
  free_provisions: ProvisionSummary[]
}

export interface InvoiceListResponse {
  count: number
  next: number | null
  previous: number | null
  results: ClientInvoiceListItem[]
}

export interface InvoiceListParams {
  page?: number
  page_size?: number
  status?: InvoiceStatus
  client?: string
  ordering?: string
}

export interface CreateInvoiceLineInput {
  description: string
  quantity: string
  unit_price: string
  tva_active: boolean
  tva_rate: string
  ordering: number
}

export interface CreateInvoiceInput {
  client: string
  case?: string | null
  issue_date: string
  due_date?: string | null
  notes?: string | null
  ras_tva_active?: boolean
  ras_tva_rate?: string
  ras_ir_active?: boolean
  ras_ir_rate?: string
  lines: CreateInvoiceLineInput[]
}

export interface UpdateInvoiceInput extends Partial<CreateInvoiceInput> {}

export interface AddPaymentInput {
  amount: string
  payment_date: string       // "YYYY-MM-DD"
  payment_method: PaymentMethod
  reference?: string | null
  notes?: string | null
}

export type CreatePaymentInput = AddPaymentInput

// Response shape for direct invoice payment (POST /invoice/{id}/payment/add)
export interface CreatePaymentResponse {
  payment: CasePayment
  invoice: {
    status: InvoiceStatus
    total_paid: string
    reste_a_payer: string
    net_a_payer: string
  }
}

export interface CasePaymentsListParams {
  type?: 'provision' | 'invoice'
  ordering?: string
}

export const invoicingService = {
  // Cabinet invoicing settings
  getSettings: async (): Promise<CabinetInvoicingSettings> => {
    const res = await client.get<CabinetInvoicingSettings>('/api/v1/invoicing-settings/')
    return res.data
  },
  updateSettings: async (data: Partial<CabinetInvoicingSettings>): Promise<CabinetInvoicingSettings> => {
    const res = await client.patch<CabinetInvoicingSettings>('/api/v1/invoicing-settings/update', data)
    return res.data
  },

  // Invoice CRUD
  list: async (params?: InvoiceListParams): Promise<InvoiceListResponse> => {
    const res = await client.get<InvoiceListResponse>('/api/v1/invoices/', { params })
    return res.data
  },
  listByCase: async (caseId: number, params?: Omit<InvoiceListParams, 'client'>): Promise<InvoiceListResponse> => {
    const res = await client.get<InvoiceListResponse>(`/api/v1/case/${caseId}/invoices/`, { params })
    return res.data
  },
  get: async (id: string): Promise<ClientInvoiceDetail> => {
    const res = await client.get<ClientInvoiceDetail>(`/api/v1/invoice/${id}/`)
    return res.data
  },
  create: async (data: CreateInvoiceInput): Promise<ClientInvoiceDetail> => {
    const res = await client.post<ClientInvoiceDetail>('/api/v1/invoice/create', data)
    return res.data
  },
  update: async (id: string, data: UpdateInvoiceInput): Promise<ClientInvoiceDetail> => {
    const res = await client.patch<ClientInvoiceDetail>(`/api/v1/invoice/${id}/update`, data)
    return res.data
  },
  send: async (id: string): Promise<ClientInvoiceDetail> => {
    const res = await client.post<ClientInvoiceDetail>(`/api/v1/invoice/${id}/send`, {})
    return res.data
  },
  remove: async (id: string): Promise<void> => {
    await client.delete(`/api/v1/invoice/${id}/remove`)
  },
  // Returns a short-lived presigned S3 URL — do not cache, fetch fresh each time
  getPdf: async (id: string): Promise<{ pdf_url: string; number: string }> => {
    const res = await client.get<{ pdf_url: string; number: string }>(`/api/v1/invoice/${id}/pdf`)
    return res.data
  },

  // Cancel an invoice
  cancel: async (id: string): Promise<ClientInvoiceDetail> => {
    const res = await client.patch<ClientInvoiceDetail>(`/api/v1/invoice/${id}/update`, { status: 'cancelled' })
    return res.data
  },

  // Honoraire automation
  createFromHonoraire: async (caseId: number): Promise<ClientInvoiceDetail> => {
    const res = await client.post<ClientInvoiceDetail>(`/api/v1/case/${caseId}/invoice/from-honoraire`, {})
    return res.data
  },
  createComplement: async (caseId: number): Promise<ClientInvoiceDetail> => {
    const res = await client.post<ClientInvoiceDetail>(`/api/v1/case/${caseId}/invoice/complement`, {})
    return res.data
  },

  // Billing summary — single source of truth for any case billing UI
  getBillingSummary: async (caseId: number): Promise<CaseBillingSummary> => {
    const res = await client.get<CaseBillingSummary>(`/api/v1/case/${caseId}/billing-summary/`)
    return res.data
  },

  // Unified case payments (provisions + invoice-linked payments)
  listCasePayments: async (caseId: number, params?: CasePaymentsListParams): Promise<CasePayment[]> => {
    const res = await client.get<CasePayment[]>(`/api/v1/case/${caseId}/payments/`, { params })
    return res.data
  },
  addCasePayment: async (caseId: number, data: AddPaymentInput): Promise<CasePayment> => {
    const res = await client.post<CasePayment>(`/api/v1/case/${caseId}/payment/add`, data)
    return res.data
  },

  // Direct invoice payment (caseless invoices — use when no case_id available)
  createPayment: async (invoiceId: string, data: CreatePaymentInput): Promise<CreatePaymentResponse> => {
    const res = await client.post<CreatePaymentResponse>(`/api/v1/invoice/${invoiceId}/payment/add`, data)
    return res.data
  },

  // Delete any payment (provision or invoice-linked) — server reverses invoice status if needed
  removePayment: async (paymentId: string): Promise<void> => {
    await client.delete(`/api/v1/case/payment/${paymentId}/remove`)
  },
}
